import { useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	Grid,
	MenuItem,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import { useItems } from "../../features/Fetch/Items";
import { BucketDefinitionConfigurationResponse, BucketNodeSnapshotResponse, BucketTypeDefinitionItem } from "../../types";
import Error from "../Error";

type DefinitionRow = BucketTypeDefinitionItem & {
	_isNew?: boolean,
	_pluralEdited?: boolean,
};

const BucketDefinitionSettings = () => {
	const repo = useItems();
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState("");
	const [config, setConfig] = useState<BucketDefinitionConfigurationResponse | null>(null);
	const [originalDefinitions, setOriginalDefinitions] = useState<DefinitionRow[]>([]);
	const [definitions, setDefinitions] = useState<DefinitionRow[]>([]);
	const [snapshot, setSnapshot] = useState<BucketNodeSnapshotResponse | null>(null);

	useEffect(() => {
		let canceled = false;

		(async () => {
			setLoading(true);
			setError("");
			try {
				const [nextConfig, nextSnapshot] = await Promise.all([
					repo.GetBucketDefinitionsAsync(),
					repo.GetBucketNodesAsync(),
				]);

				if (canceled) return;

				const normalized = MarkExisting(nextConfig.definitions);
				setConfig(nextConfig);
				setOriginalDefinitions(normalized);
				setDefinitions(normalized);
				setSnapshot(nextSnapshot);
			} catch (ex: any) {
				if (!canceled) {
					setError(ex?.toString?.() ?? "Unable to load bucket definitions.");
				}
			} finally {
				if (!canceled) setLoading(false);
			}
		})();

		return () => {
			canceled = true;
		};
		// repo methods are recreated by useItems(); load once on mount for this settings screen.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const isDirty = useMemo(() => {
		return JSON.stringify(StripTransientFields(definitions)) !== JSON.stringify(StripTransientFields(originalDefinitions));
	}, [definitions, originalDefinitions]);

	return (
		<Grid item xs={12}>
			<Paper sx={{ p: 2, mt: 4 }}>
				<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, mb: 2, flexWrap: "wrap" }}>
					<Box>
						<Typography variant="h6" sx={{ fontWeight: "bold" }}>Bucket Definitions</Typography>
						<Typography variant="body2" color="text.secondary">
							Configure the bucket types available to this family, including labels, hierarchy, and transaction selection rules.
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Source: {config?.isOverride ? "Family override" : "Family type defaults"}
						</Typography>
						{snapshot && (
							<Typography variant="body2" color="text.secondary">
								Bucket nodes: {snapshot.nodeCount} nodes / {snapshot.relationCount} relations
							</Typography>
						)}
					</Box>
					<Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
						<Button
							variant="contained"
							className="bg-success"
							disabled={loading || saving || !isDirty}
							onClick={async () => {
								setSaving(true);
								setError("");
								try {
									const nextConfig = await repo.SaveBucketDefinitionsAsync({ definitions: StripTransientFields(definitions) });
									const normalized = MarkExisting(nextConfig.definitions);
									setConfig(nextConfig);
									setOriginalDefinitions(normalized);
									setDefinitions(normalized);
								} catch (ex: any) {
									setError(ex?.toString?.() ?? "Unable to save bucket definitions.");
								} finally {
									setSaving(false);
								}
							}}
						>
							{saving ? "Saving..." : "Save Bucket Definitions"}
						</Button>
					</Box>
				</Box>

				<Typography variant="body2" sx={{ mb: 1.5 }}>
					These definitions drive non-financial bucket trees. A bucket without a parent is treated as a root type automatically, and min 0 means the bucket is optional when items exist.
				</Typography>
				<Typography variant="body2" sx={{ mb: 1.5 }}>
					When "Requires Budget Mapping" is on, budgets will eventually maintain the allowed nodes for that type and transaction editors will only allow mapped nodes for the selected budget.
				</Typography>

				<Box sx={{ overflowX: "auto" }}>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell sx={{ fontWeight: "bold" }}>Key</TableCell>
								<TableCell sx={{ fontWeight: "bold" }}>Label</TableCell>
								<TableCell sx={{ fontWeight: "bold" }}>Plural</TableCell>
								<TableCell sx={{ fontWeight: "bold" }}>Parent</TableCell>
								<TableCell sx={{ fontWeight: "bold" }}>Min</TableCell>
								<TableCell sx={{ fontWeight: "bold" }}>Max</TableCell>
								<TableCell sx={{ fontWeight: "bold" }}>Sort</TableCell>
								<TableCell sx={{ fontWeight: "bold" }}>Flags</TableCell>
								<TableCell sx={{ fontWeight: "bold" }}>Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{definitions.map((definition, index) => {
								const parentOptions = definitions.filter((_, optionIndex) => optionIndex !== index && !!definitions[optionIndex].bucketTypeKey?.trim());
								return (
									<TableRow key={index}>
										<TableCell>
											<Typography variant="body2" sx={{ fontFamily: "monospace" }}>
												{definition.bucketTypeKey || <Box component="span" sx={{ color: "text.disabled" }}>(auto)</Box>}
											</Typography>
										</TableCell>
										<TableCell>
											<TextField
												size="small"
												fullWidth
												value={definition.label}
												onChange={(e) => updateDefinition(index, deriveLabelChange(definitions[index], e.target.value))}
											/>
										</TableCell>
										<TableCell>
											<TextField
												size="small"
												fullWidth
												value={definition.pluralLabel}
												onChange={(e) => updateDefinition(index, { pluralLabel: e.target.value, _pluralEdited: true })}
											/>
										</TableCell>
										<TableCell sx={{ minWidth: 180 }}>
											<Select
												size="small"
												fullWidth
												value={definition.parentBucketTypeKey ?? ""}
												onChange={(e) => updateDefinition(index, {
													parentBucketTypeKey: e.target.value ? String(e.target.value) : null,
													requiresBudgetMapping: false, // force disable budget mapping when a parent is selected, as child nodes should inherit allowed budgets from the parent.
												})}
											>
												<MenuItem value="">None</MenuItem>
												{parentOptions.map(option => (
													<MenuItem key={`${option.bucketTypeKey}-${index}`} value={option.bucketTypeKey}>{option.bucketTypeKey}</MenuItem>
												))}
											</Select>
										</TableCell>
										<TableCell>
											<TextField
												size="small"
												type="number"
												inputProps={{ min: 0 }}
												value={definition.minSelectionsPerTransaction}
												onChange={(e) => updateDefinition(index, { minSelectionsPerTransaction: ParseInteger(e.target.value, 0) })}
												sx={{ width: 90 }}
											/>
										</TableCell>
										<TableCell>
											<TextField
												size="small"
												type="number"
												inputProps={{ min: 0 }}
												value={definition.maxSelectionsPerTransaction ?? ""}
												onChange={(e) => updateDefinition(index, { maxSelectionsPerTransaction: ParseNullableInteger(e.target.value) })}
												sx={{ width: 90 }}
											/>
										</TableCell>
										<TableCell>
											<TextField
												size="small"
												type="number"
												value={definition.sortOrder}
												onChange={(e) => updateDefinition(index, { sortOrder: ParseInteger(e.target.value, 0) })}
												sx={{ width: 90 }}
											/>
										</TableCell>
										<TableCell sx={{ minWidth: 200 }}>
											<Box sx={{ display: "flex", flexDirection: "column" }}>
												<FormControlLabel
													control={<Checkbox checked={definition.isEnabled} onChange={(e) => updateDefinition(index, { isEnabled: e.target.checked })} />}
													label="Enabled"
												/>
												<FormControlLabel
													control={<Checkbox checked={definition.requiresBudgetMapping} onChange={(e) => updateDefinition(index, { requiresBudgetMapping: e.target.checked })} />}
													label="Requires Budget Mapping"
													disabled={!!definition.parentBucketTypeKey}
												/>
											</Box>
										</TableCell>
										<TableCell>
											{(() => {
												const nodeCount = (snapshot?.nodes ?? []).filter(n => n.bucketTypeKey?.toLowerCase() === definition.bucketTypeKey?.toLowerCase()).length;
												const hasNodes = !!definition.bucketTypeKey?.trim() && nodeCount > 0;
												return (
													<Button
														variant="text"
														color="error"
														disabled={definitions.length <= 1 || hasNodes}
														title={hasNodes ? `Cannot remove: ${nodeCount} node${nodeCount === 1 ? "" : "s"} exist for this type` : undefined}
														onClick={() => removeDefinition(index)}
													>
														Remove
													</Button>
												);
											})()}
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				</Box>

				<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2, flexWrap: "wrap", gap: 1 }}>
					<Button
						variant="outlined"
						disabled={loading || saving}
						onClick={() => {
							setDefinitions(current => ([
								...current,
								CreateEmptyDefinition(current),
							]));
						}}
					>
						Add Bucket Type
					</Button>
					<Typography variant="body2" color="text.secondary">
						Changes here only affect this family after save.
					</Typography>
				</Box>

				{loading && <Typography variant="body2" sx={{ mt: 2 }}>Loading bucket definitions...</Typography>}
				{!!error && <Box sx={{ mt: 2 }}><Error Text={error} /></Box>}
			</Paper>
		</Grid>
	);

	function updateDefinition(index: number, partial: Partial<DefinitionRow>) {
		setDefinitions(current => NormalizeDefinitions(current.map((definition, currentIndex) => {
			if (currentIndex !== index) return definition;
			return {
				...definition,
				...partial,
			};
		})));
	}

	function removeDefinition(index: number) {
		setDefinitions(current => {
			const removedKey = current[index]?.bucketTypeKey;
			return NormalizeDefinitions(current
				.filter((_, currentIndex) => currentIndex !== index)
				.map(definition => ({
					...definition,
					parentBucketTypeKey: definition.parentBucketTypeKey === removedKey ? null : definition.parentBucketTypeKey,
				})));
		});
	}
};

function CreateEmptyDefinition(current: DefinitionRow[]): DefinitionRow {
	const nextSort = current.length === 0 ? 10 : Math.max(...current.map(definition => definition.sortOrder || 0)) + 10;
	return {
		bucketTypeKey: "",
		label: "",
		pluralLabel: "",
		parentBucketTypeKey: null,
		isIndependent: true,
		isEnabled: true,
		requiresBudgetMapping: false,
		minSelectionsPerTransaction: 0,
		maxSelectionsPerTransaction: null,
		sortOrder: nextSort,
		_isNew: true,
		_pluralEdited: false,
	};
}

function NormalizeDefinitions(definitions: DefinitionRow[]): DefinitionRow[] {
	return ApplyDerivedDefinitionState(SanitizeDefinitions(definitions));
}

function MarkExisting(definitions: BucketTypeDefinitionItem[]): DefinitionRow[] {
	return NormalizeDefinitions(definitions.map(d => ({ ...d, _isNew: false, _pluralEdited: true })));
}

function StripTransientFields(definitions: DefinitionRow[]): BucketTypeDefinitionItem[] {
	return SanitizeDefinitions(definitions).map(({ _isNew, _pluralEdited, ...rest }: DefinitionRow) => rest as BucketTypeDefinitionItem);
}

function SanitizeDefinitions(definitions: DefinitionRow[]): DefinitionRow[] {
	return definitions.map(definition => ({
		...definition,
		bucketTypeKey: (definition.bucketTypeKey ?? "").trim().toLowerCase(),
		label: (definition.label ?? "").trim(),
		pluralLabel: (definition.pluralLabel ?? "").trim(),
		parentBucketTypeKey: definition.parentBucketTypeKey ? definition.parentBucketTypeKey.trim().toLowerCase() : null,
		isIndependent: definition.isIndependent ?? false,
		isEnabled: definition.isEnabled ?? true,
		requiresBudgetMapping: definition.requiresBudgetMapping ?? false,
		minSelectionsPerTransaction: definition.minSelectionsPerTransaction ?? 0,
		maxSelectionsPerTransaction: definition.maxSelectionsPerTransaction ?? null,
		sortOrder: definition.sortOrder ?? 0,
	}));
}

function ApplyDerivedDefinitionState(definitions: DefinitionRow[]): DefinitionRow[] {
	return definitions.map(definition => {
		const parentBucketTypeKey = definition.parentBucketTypeKey ?? null;
		const isIndependent = !parentBucketTypeKey;

		return {
			...definition,
			parentBucketTypeKey,
			isIndependent,
		};
	});
}

function deriveLabelChange(current: DefinitionRow, nextLabel: string): Partial<DefinitionRow> {
	const patch: Partial<DefinitionRow> = { label: nextLabel };
	if (current._isNew) {
		patch.bucketTypeKey = NormalizeBucketKeyInput(nextLabel);
		if (!current._pluralEdited) {
			patch.pluralLabel = Pluralize(nextLabel);
		}
	}
	return patch;
}

function Pluralize(label: string): string {
	const trimmed = (label ?? "").trim();
	if (!trimmed) return "";
	const lower = trimmed.toLowerCase();
	if (/(s|x|z|ch|sh)$/.test(lower)) return trimmed + "es";
	if (/[^aeiou]y$/.test(lower)) return trimmed.slice(0, -1) + "ies";
	return trimmed + "s";
}

function NormalizeBucketKeyInput(value: string) {
	return value.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_-]/g, "");
}

function ParseInteger(value: string, fallback: number) {
	const parsed = parseInt(value, 10);
	return Number.isFinite(parsed) ? parsed : fallback;
}

function ParseNullableInteger(value: string) {
	if (value === "") return null;
	const parsed = parseInt(value, 10);
	return Number.isFinite(parsed) ? parsed : null;
}

export default BucketDefinitionSettings;